// Converts the free-form JSON result object returned by the AI Ticket
// endpoint into a single Markdown document.
//
// Each top-level key becomes a `## Heading`. Values render per type:
//   string   → paragraph (already markdown; embedded fences kept as-is)
//   string[] → bullet list
//   number/boolean → inline paragraph
//   object   → `### key` subsections (one level of recursion)
//   array of objects → JSON fenced block (fallback)
//
// Used by the ticket panel's Copy-as-Markdown and Download-.md actions,
// and by any caller that wants a human-readable rendering.

export function ticketToMarkdown(result: Record<string, unknown>): string {
  if (!result) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(result)) {
    const heading = humaniseKey(key);
    parts.push(`## ${heading}`);
    parts.push(renderValue(value, 3));
    parts.push(''); // blank line between sections
  }
  return parts.join('\n').trim() + '\n';
}

function renderValue(value: unknown, nextHeadingLevel: number): string {
  if (value === null || value === undefined) return '_(not provided)_';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '_(empty)_';
    const allScalar = value.every(
      v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    );
    if (allScalar) {
      return value.map(v => `- ${String(v).trim()}`).join('\n');
    }
    // Array of objects: fallback to fenced JSON so nothing is lost.
    return '```json\n' + JSON.stringify(value, null, 2) + '\n```';
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (nextHeadingLevel > 6) {
      // Limit heading depth; fall back to JSON block.
      return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
    }
    const lines: string[] = [];
    const prefix = '#'.repeat(nextHeadingLevel);
    for (const [k, v] of Object.entries(obj)) {
      lines.push(`${prefix} ${humaniseKey(k)}`);
      lines.push(renderValue(v, nextHeadingLevel + 1));
      lines.push('');
    }
    return lines.join('\n').trim();
  }

  return String(value);
}

export function humaniseKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}
